import React, { ReactNode } from 'react';
import _ from 'lodash';
import RouteType from 'src/core/models/route.model';
import RouteWithLayout from 'src/shared/hoc/route-with-layout.hoc';
import moment from 'moment';
import { QR_CODE_FONT_SIZE, QR_EXPORT_WIDTH, QR_PRINT_DPI, QR_SIZE } from 'src/core/utils/constants';

const FileSaver = require('file-saver');

export const buildRoutes = (routes: Array<RouteType>): ReactNode[] => {
    const elementRoute: ReactNode[] = [];
    if (_.isEmpty(routes)) {
        return elementRoute;
    }

    const length = routes.length;
    for (let index = 0; index < length; index++) {
        const route = routes[index];

        elementRoute.push(
            <RouteWithLayout
                key={route.name + index}
                exact={route.exact}
                path={route.path}
                component={route.main}
                withNav={route.withNav}
            />
        )
    }

    return elementRoute;
}

/**
 * Converts a string to its html characters completely.
 *
 * @param {String} str String with unescaped HTML characters
 **/
export const encode = (str: any): string => {
    const buf = [];

    for (let i = str.length - 1; i >= 0; i--) {
        buf.unshift(['&#', str[i].charCodeAt(), ';'].join(''));
    }

    return buf.join('');
};

/**
 * Converts an html characterSet into its original character.
 *
 * @param {String} str htmlSet entities
 **/
export const decode = (str: string): string => {
    return str.replace(/&#(\d+);/g, function (match, dec) {
        return String.fromCharCode(dec);
    });
};

export const isNaNDate = (value: Date): boolean => {
    if (_.isNil(value) || _.isNaN(value.getDate()) || _.isNaN(value.getMonth()) || _.isNaN(value.getFullYear())) {
        return true;
    }

    return false;
}

export const currencyFormatter = (value: number, locale = 'vi', currency = 'VND', decimalDigits = 0): string => {
    const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: decimalDigits,
        // the default value for minimumFractionDigits depends on the currency
        // and is usually already 2
    });

    return _.isFinite(value) ? formatter.format(value) : '';
};

export const formatNumber = (num: number) => {
    if (!num) {
        return '0';
    }
    return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
}

export const roundUp = (num: number, precision: number): number | string => {
    if (!_.isFinite(num)) return '';

    precision = Math.pow(10, precision);
    return Math.ceil(num * precision) / precision;
};

export const roundDown = (num: number, precision: number): number | string => {
    if (!_.isFinite(num)) return '';

    precision = Math.pow(10, precision);
    return Math.floor(num * precision) / precision;
};

export function setLocalStorage(key: string, data = {}): void {
    localStorage.setItem(key, JSON.stringify(data || {}));
}

export function getLocalStorage<T = any>(key: string): T {
    try {
        return JSON.parse(localStorage.getItem(key) || '{}');
    } catch (error) {
        return {} as any;
    }
}

export function removeLocalStorage(key: string): void {
    localStorage.removeItem(key);
}

export function clearLocalStorage(): void {
    localStorage.clear();
}

export function setSessionStorage(key: string, data = {}): void {
    sessionStorage.setItem(key, JSON.stringify(data || {}));
}

export function getSessionStorage<T = any>(key: string): T {
    try {
        return JSON.parse(sessionStorage.getItem(key) || '{}');
    } catch (error) {
        return {} as any;
    }
}

export function removeSessionStorage(key: string): void {
    sessionStorage.removeItem(key);
}

export function clearSessionStorage(): void {
    sessionStorage.clear();
}

export const formatDateForApi = (date: Date): string => {
    return moment(date).format('YYYY-MM-DD');
}

export const getTextFromHtml = (html: string) => {
    return html.replace(/<[^>]*>?/gm, '');
}

export const getRandomKey = () => `${Math.floor((Math.random() * 1000))}`;

// Co chu mac dinh (QR_CODE_FONT_SIZE) vua khit cho ma 14 ky tu trong khung
// QR_SIZE. Ma dai hon se tran ra ngoai khung nen phai thu co chu lai, va
// thu co chu lai cho vua. He so 0.58 la ty le be ngang/chieu cao trung binh
// cua font Arial Rounded MT Bold.
export const getQrCodeFontSize = (code: string): number => {
    if (!code) {
        return QR_CODE_FONT_SIZE;
    }
    const fitted = Math.floor(QR_SIZE / (code.length * 0.58));
    return Math.min(QR_CODE_FONT_SIZE, fitted);
}

// Noi dung nhung vao ma QR. Bo "https://" va dung duong dan ngan /d/ de ma
// it o nhat co the: QR duoc in o kho 15mm nen moi o chi con ~0.4mm, cang
// nhieu o thi dien thoai cang kho quet. Trinh duyet va app camera deu tu
// them https:// khi mo, nen bo di khong anh huong gi.
//   /public/document/ + https://  -> 45 o  (0.33 mm/o, duoi chuan)
//   /d/ khong co https://         -> 37 o  (0.41 mm/o, dat chuan)
export const buildQrValue = (code: string): string => {
    return `${window.location.host}/d/${code}`;
}

// Ghi DPI vao file PNG qua chunk pHYs. Khong co chunk nay, phan mem in se
// doan DPI (thuong 96) va in sai kho: anh 185px se ra 49mm thay vi 15.7mm.
const withPngDpi = (dataUrl: string, dpi: number): string => {
    const binary = atob(dataUrl.split(',')[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    // pHYs: 9 byte du lieu - pixel/met theo truc X, truc Y, va don vi (1 = met).
    const perMetre = Math.round(dpi / 0.0254);
    const chunk = new Uint8Array(21);
    const view = new DataView(chunk.buffer);
    view.setUint32(0, 9);
    chunk.set([0x70, 0x48, 0x59, 0x73], 4); // 'pHYs'
    view.setUint32(8, perMetre);
    view.setUint32(12, perMetre);
    chunk[16] = 1;

    // CRC32 tinh tren ten chunk + du lieu.
    let crc = 0xffffffff;
    for (let i = 4; i < 17; i++) {
        crc ^= chunk[i];
        for (let b = 0; b < 8; b++) {
            crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
        }
    }
    view.setUint32(17, (crc ^ 0xffffffff) >>> 0);

    // Chen ngay sau IHDR (8 byte chu ky + 25 byte IHDR).
    const out = new Uint8Array(bytes.length + chunk.length);
    out.set(bytes.subarray(0, 33), 0);
    out.set(chunk, 33);
    out.set(bytes.subarray(33), 33 + chunk.length);

    let encoded = '';
    for (let i = 0; i < out.length; i++) {
        encoded += String.fromCharCode(out[i]);
    }
    return `data:image/octet-stream;base64,${btoa(encoded)}`;
}

export const downloadQR = (code: string) => {
    const container: any = document.getElementById('document-qr-code');
    const sourceCanvas: HTMLCanvasElement | null = container?.querySelector('canvas');

    // Khong dung html2canvas: no chup lai canvas roi thu ve bang bo loc lam
    // mo, khien canh cac o QR nhoe thanh mang xam. Doc thang pixel cua canvas
    // ma qrcode.react da ve roi lay mau theo diem, nen khong qua bo loc nao.
    if (!sourceCanvas) {
        return;
    }

    const sourceCtx = sourceCanvas.getContext('2d');
    if (!sourceCtx) {
        return;
    }

    // Canvas nguon rong QR_SIZE x devicePixelRatio, tuc may Retina cho canvas
    // to gap doi may thuong. Lay mau ve dung QR_EXPORT_WIDTH de file giong
    // nhau tren moi may - day la dieu kien de kho in luon chinh xac.
    const source = sourceCtx.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
    const qrSize = QR_EXPORT_WIDTH;
    const ratio = sourceCanvas.width / qrSize;

    const fontSize = Math.round(qrSize * getQrCodeFontSize(code) / QR_SIZE);
    const textHeight = Math.ceil(fontSize * 1.5);

    const canvas = document.createElement('canvas');
    canvas.width = qrSize;
    canvas.height = qrSize + textHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return;
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Lay mau diem: moi pixel dich nhan mau cua pixel nguon tai tam o tuong
    // ung. Vi moi o QR la mot khoi mau dong nhat, cach nay tai tao dung o do
    // ma khong tron mau giua hai o ke nhau - khac han phep thu co bo loc.
    const target = ctx.createImageData(qrSize, qrSize);
    for (let y = 0; y < qrSize; y++) {
        const sy = Math.min(sourceCanvas.height - 1, Math.floor((y + 0.5) * ratio));
        for (let x = 0; x < qrSize; x++) {
            const sx = Math.min(sourceCanvas.width - 1, Math.floor((x + 0.5) * ratio));
            const from = (sy * sourceCanvas.width + sx) * 4;
            const to = (y * qrSize + x) * 4;
            target.data[to] = source.data[from];
            target.data[to + 1] = source.data[from + 1];
            target.data[to + 2] = source.data[from + 2];
            target.data[to + 3] = 255;
        }
    }
    ctx.putImageData(target, 0, 0);

    ctx.fillStyle = '#000000';
    ctx.font = `${fontSize}px "Arial Rounded MT Bold", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(code.replaceAll('_', '/'), canvas.width / 2, qrSize + textHeight / 2);

    const downloadLink = document.createElement('a');
    downloadLink.href = withPngDpi(canvas.toDataURL('image/png'), QR_PRINT_DPI);
    downloadLink.download = `${code}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
}

export const downloadFile = (url: string, fileName: string) => {
    const extension = fileName.split('.').pop();
    if (extension?.toLowerCase() === 'pdf') {
        downloadPDF(url, fileName);
        return;
    }
    FileSaver.saveAs(url, fileName);
}

export const downloadPDF = (url: string, fileName: string) => {
    const oReq = new XMLHttpRequest();
    oReq.open('GET', url, true);
    oReq.responseType = 'blob';
    oReq.onload = function() {
        const file = new Blob([oReq.response], {
            type: 'application/pdf'
        });
        FileSaver.saveAs(file, fileName);
    };
    oReq.send();
}